import Grid from '@mui/system/Grid';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { CardActionArea } from 'src/components/Card';
import { Stack } from 'src/components/Stack';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { PaymentOption } from 'src/models/cart/_types';
import { getPaymentOptionDetails } from 'src/models/cart/utils';
import { Product } from 'src/models/product/_types';
import { PurchaseProductDialog } from 'src/pages/Purchase/components/PurchaseProductDialog';
import { Identifier } from 'src/types/utilityTypes';
import { formatCurrency } from 'src/utils/numberFormatter';
import { titleize } from 'src/utils/stringFormatter';
import toast from 'src/utils/toast';

import { usePosContext } from '../PosContext';

import { PaymentOptionSelectionDialog } from './PaymentOptionSelectionDialog';

interface PosProductCardProps {
  itemId: Identifier;
  itemType: string;
  addToCartCallback?: () => void;
}

export const PosProductCard = ({
  itemId,
  itemType,
  addToCartCallback,
}: PosProductCardProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pointOfSale.productCard',
  });
  const { session, isDarkmode } = useOptionalSession();
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [paymentOptionDialogOpen, setPaymentOptionDialogOpen] = useState(false);
  const defaultProductImage = isDarkmode()
    ? session?.darkmodeLogoUrl
    : session?.logoUrl;

  const {
    posLocation,
    cart,
    addToCart,
    getItemConfig,
    getItem,
    isInCart,
    isInStock,
    updateQuantity,
  } = usePosContext();

  const item = getItem(itemId, itemType);
  const itemConfig = getItemConfig(itemId);
  const isOutOfStock = !isInStock(itemId, itemType);

  const [cartItemIndex, cartItem] = useMemo(() => {
    const index = cart?.cartItems.findIndex(
      (item) => item.itemId?.toString() === itemId.toString()
    );

    if (index === undefined) {
      return [undefined, undefined];
    }

    const cartItem = cart?.cartItems[index];

    return [index, cartItem];
  }, [cart, itemId]);

  const getPriceDisplay = () => {
    if (!item) {
      return '';
    }

    if (item['productVariants'] && item['productVariants'].length > 0) {
      const prices = item['productVariants'].map((variant) => variant.price);
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);

      if (minPrice === maxPrice) {
        return formatCurrency(minPrice, {
          currency: session?.defaultCurrency,
          cents: true,
        });
      } else {
        return `${formatCurrency(minPrice, {
          currency: session?.defaultCurrency,
          cents: true,
        })} - ${formatCurrency(maxPrice, {
          currency: session?.defaultCurrency,
          cents: true,
        })}`;
      }
    } else {
      if (item['paymentOptions'] && item['paymentOptions'].length > 0) {
        if (item['paymentOptions'].length > 1) {
          return 'Multiple Payment Options';
        }

        return getPaymentOptionDetails(item['paymentOptions'][0]);
      }
    }
  };

  const getImageUrl = () => {
    if (!item) {
      return '';
    }

    if (item['imageUrl']) {
      return item['imageUrl'];
    } else if (item['smallPlanImage']) {
      return item['smallPlanImage'];
    } else {
      return defaultProductImage;
    }
  };

  const handleAddToCart = async (item: any, options?: any) => {
    await addToCart(item, options);
    if (addToCartCallback) {
      addToCartCallback();
    }
  };

  const handlePaymentOptionSelect = async (paymentOption: PaymentOption) => {
    const options = {
      type: itemType,
      paymentOption,
    };
    await handleAddToCart(item, options);
  };

  const handleCardClick = () => {
    if (shouldDisableCard || !item) {
      return;
    }

    // Check if item has multiple payment options and needs selection
    const hasMultiplePaymentOptions =
      (item as any)?.paymentOptions &&
      (item as any).paymentOptions.length > 1 &&
      itemType !== 'product'; // Products still use the existing product dialog

    switch (itemType) {
      case 'product':
        setProductDialogOpen(true);
        break;
      case 'product-variant': {
        if (isInCart(itemId)) {
          const index = cartItemIndex;
          if (index !== undefined && cartItem) {
            updateQuantity(index, cartItem.quantity + 1);
          }
        } else {
          handleAddToCart(item);
        }
        break;
      }
      case 'group':
        if (hasMultiplePaymentOptions) {
          setPaymentOptionDialogOpen(true);
        } else {
          handleAddToCart(item, {
            type: 'group',
          });
        }
        break;
      case 'package':
        if (hasMultiplePaymentOptions) {
          setPaymentOptionDialogOpen(true);
        } else {
          handleAddToCart(item, {
            type: 'package',
          });
        }
        break;
      case 'plan':
        if (hasMultiplePaymentOptions) {
          setPaymentOptionDialogOpen(true);
        } else {
          handleAddToCart(item, {
            type: 'plan',
          });
        }
        break;
      default:
        toast.error('Invalid item type');
    }
  };

  const shouldShowCard = () => {
    if (!item) {
      return false;
    }

    if (item['paymentOptions'].length === 0) {
      return false;
    }

    return true;
  };

  const isOneTimePurchase = useMemo(() => {
    return ['group', 'plan', 'workoutplan'].includes(
      itemConfig?.itemType?.toLowerCase() || ''
    );
  }, [itemConfig?.itemType]);

  const shouldDisableCard = useMemo(() => {
    if (isOutOfStock) {
      return true;
    }

    if (isOneTimePurchase) {
      return isInCart(itemId);
    }
    return false;
  }, [isOutOfStock, isOneTimePurchase, isInCart, itemId]);

  const getTooltipTitle = () => {
    if (!item) {
      return '';
    }

    if (isOutOfStock) {
      return t('outOfStock');
    }

    if (isInCart(itemId) && isOneTimePurchase) {
      return t('alreadyInCart');
    }

    return titleize(item['name']);
  };

  if (!shouldShowCard()) {
    return null;
  }

  return (
    <Grid
      key={`${itemId}`}
      size={{ xs: 12, sm: 6, md: 4, lg: 4, xl: 3 }}
      height="100px"
    >
      <Tooltip title={getTooltipTitle()}>
        <CardActionArea
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            p: 2,
            minHeight: '100px',
            bgcolor: 'background.level2',
            borderRadius: 2,
            cursor: shouldDisableCard ? 'not-allowed' : 'pointer',
            opacity: shouldDisableCard ? 0.5 : 1,
            transition: 'background-color 0.3s ease',
            overflow: 'hidden',
            width: '100%',
            '&:hover': {
              bgcolor: 'background.level3',
              transition: 'background-color 0.3s ease',
            },
          }}
          onClick={handleCardClick}
        >
          <Stack
            spacing={2}
            direction="row"
            alignItems="top"
            sx={{ width: '100%' }}
          >
            <Box
              component="img"
              src={getImageUrl()}
              alt={item?.['name']}
              sx={{
                p: 0,
                m: 0,
                width: '50px',
                height: '50px',
                borderRadius: 1,
              }}
            />

            <Stack justifyContent="space-between" sx={{ flex: 1, minWidth: 0 }}>
              <Stack>
                <Typography
                  color="text.primary"
                  variant="h6"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item?.['name']}
                </Typography>
                <Typography color="text.primary" variant="body2">
                  {getPriceDisplay()}
                </Typography>
              </Stack>
              {isOutOfStock && (
                <Typography
                  variant="caption"
                  color="error.main"
                  fontWeight="600"
                  sx={{ textTransform: 'uppercase' }}
                >
                  {t('outOfStock')}
                </Typography>
              )}
            </Stack>
          </Stack>
        </CardActionArea>
      </Tooltip>

      <PurchaseProductDialog
        open={productDialogOpen}
        onClose={() => setProductDialogOpen(false)}
        product={item as Product}
        cart={cart}
        locationId={posLocation?.id}
        addToCart={handleAddToCart}
        publishingChannel="staff"
        canManageAll={false}
      />

      <PaymentOptionSelectionDialog
        open={paymentOptionDialogOpen}
        onClose={() => setPaymentOptionDialogOpen(false)}
        item={item}
        paymentOptions={(item as any)?.paymentOptions || []}
        onSelect={handlePaymentOptionSelect}
      />
    </Grid>
  );
};
